import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../utils/supabase';

const AdminSidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/user-login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const navigationItems = [
    {
      title: 'Dashboard',
      path: '/admin-dashboard',
      icon: 'LayoutDashboard',
      description: 'Platform overview'
    },
    {
      title: 'Revenue Analytics',
      path: '/admin-revenue-analytics',
      icon: 'DollarSign',
      description: 'Financial insights'
    },
    {
      title: 'User Management',
      path: '/admin-user-management',
      icon: 'Users',
      description: 'Manage users & roles'
    },
    {
      title: 'Moderation',
      path: '/admin-moderation-dashboard',
      icon: 'Shield',
      description: 'Content moderation'
    },
    {
      title: 'Studio Management',
      path: '/admin-studio-management',
      icon: 'Building',
      description: 'Studios & approvals'
    },
    {
      title: 'Studio Details',
      path: '/admin-studio-details',
      icon: 'Settings',
      description: 'API keys & domains'
    },
    {
      title: 'Game Reviews',
      path: '/admin-game-reviews',
      icon: 'CheckSquare',
      description: 'Approve pending games'
    },
    {
      title: 'Game Management',
      path: '/game-content-management',
      icon: 'Gamepad2',
      description: 'Content & publishing'
    },
    {
      title: 'Contact Management',
      path: '/admin-contact-management',
      icon: 'MessageSquare',
      description: 'Support tickets & inquiries'
    },
    {
      title: 'Email Templates',
      path: '/admin-email-templates',
      icon: 'Mail',
      description: 'Manage email templates'
    },
    {
      title: 'Site Messages',
      path: '/admin-site-messages',
      icon: 'Bell',
      description: 'Site-wide messaging'
    }
  ];


  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-40 lg:block ${
        isCollapsed ? 'lg:w-16' : 'lg:w-72'
      } transition-all duration-300 ease-in-out`}>
        <div className="flex h-full flex-col bg-card border-r border-border">
          {/* Header */}
          <div className="flex h-16 items-center justify-between px-4 border-b border-border">
            {!isCollapsed && (
              <Link to="/admin-dashboard" className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                  <Icon name="Gamepad2" size={20} color="white" />
                </div>
                <div>
                  <span className="text-sm font-bold text-foreground">Solis Games</span>
                  <p className="text-xs text-muted-foreground">Admin Portal</p>
                </div>
              </Link>
            )}
            <Button
              variant="ghost"
              size="sm"
              iconName={isCollapsed ? "ChevronRight" : "ChevronLeft"}
              onClick={() => setIsCollapsed(!isCollapsed)}
            />
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-2">
            <div className="space-y-2">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === item.path
                      ? 'bg-primary/10 text-primary border border-primary/20' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                  title={isCollapsed ? item.title : undefined}
                >
                  <Icon name={item.icon} size={20} />
                  {!isCollapsed && (
                    <div>
                      <div>{item.title}</div>
                      <div className="text-xs text-muted-foreground">{item.description}</div>
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </nav>

          {/* Admin User Info */}
          <div className="px-4 py-4 border-t border-border mt-auto">
            {isCollapsed ? (
              <Button
                variant="ghost"
                size="sm"
                iconName="LogOut"
                className="w-full"
                onClick={handleLogout}
                title="Logout"
              />
            ) : (
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="Shield" size={16} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {user?.full_name || user?.email?.split('@')[0] || 'Admin'}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user?.email || 'Not logged in'}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="LogOut"
                  onClick={handleLogout}
                  title="Logout"
                />
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile Top Navigation */}
      <nav className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="flex items-center justify-between px-4 h-16">
          <Link to="/admin-dashboard" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
              <Icon name="Gamepad2" size={20} color="white" />
            </div>
            <div>
              <span className="text-sm font-bold text-foreground">Solis Games</span>
              <p className="text-xs text-muted-foreground">Admin Portal</p>
            </div>
          </Link>

          <div className="flex items-center space-x-2">
            <Button variant="ghost" size="sm" iconName="Bell" />
            <Button variant="ghost" size="sm" iconName="Shield" />
          </div>
        </div>
      </nav>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-t border-border">
        <div className="flex items-center justify-around py-2">
          <Link 
            to="/admin-dashboard" 
            className={`flex flex-col items-center space-y-1 px-3 py-2 rounded-lg transition-colors ${
              location.pathname === '/admin-dashboard' ?'text-primary' :'text-muted-foreground'
            }`}
          >
            <Icon name="LayoutDashboard" size={20} />
            <span className="text-xs font-medium">Dashboard</span>
          </Link>
          
          <Link
            to="/game-content-management"
            className={`flex flex-col items-center space-y-1 px-3 py-2 rounded-lg transition-colors ${
              location.pathname === '/game-content-management' ?'text-primary' :'text-muted-foreground'
            }`}
          >
            <Icon name="Gamepad2" size={20} />
            <span className="text-xs font-medium">Games</span>
          </Link>

          <Link
            to="/admin-user-management"
            className={`flex flex-col items-center space-y-1 px-3 py-2 rounded-lg transition-colors ${
              location.pathname === '/admin-user-management' ?'text-primary' :'text-muted-foreground'
            }`}
          >
            <Icon name="Users" size={20} />
            <span className="text-xs font-medium">Users</span>
          </Link>

          <Link
            to="/admin-revenue-analytics"
            className={`flex flex-col items-center space-y-1 px-3 py-2 rounded-lg transition-colors ${
              location.pathname === '/admin-revenue-analytics' ?'text-primary' :'text-muted-foreground'
            }`}
          >
            <Icon name="DollarSign" size={20} />
            <span className="text-xs font-medium">Revenue</span>
          </Link>
        </div>
      </nav>
    </>
  );
};

export default AdminSidebar;
